import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import RangeSlider from "react-range-slider-input";
import "react-range-slider-input/dist/style.css";
import { formatNumber } from "../../Util/priceFormatter";

export const PriceSliderDropdown = ({ filters, setFilters, isOpen, onClose, inputRef }) => {
  const [localAdvertiseType, setLocalAdvertiseType] = useState(filters.advertise_type || "Mieten");
  const [priceRange, setPriceRange] = useState([
    filters.minPrice || (filters.advertise_type === "Mieten" ? 100 : 10000),
    filters.maxPrice || (filters.advertise_type === "Mieten" ? 5000 : 5000000)
  ]);
  const dropdownRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  // Berechne Position beim Öffnen
  useEffect(() => {
    if (isOpen && inputRef?.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        left: rect.left,
      });
    }
  }, [isOpen, inputRef]);

  // Aktualisiere lokalen State wenn Filter sich ändern
  useEffect(() => {
    setLocalAdvertiseType(filters.advertise_type || "Mieten");
    setPriceRange([
      filters.minPrice || (filters.advertise_type === "Mieten" ? 100 : 10000),
      filters.maxPrice || (filters.advertise_type === "Mieten" ? 5000 : 5000000)
    ]);
  }, [filters.advertise_type, filters.minPrice, filters.maxPrice]);

  // Schließe beim Klick außerhalb
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        if (inputRef?.current && !inputRef.current.contains(event.target)) {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen, onClose, inputRef]);

  const handleAdvertiseTypeChange = (type) => {
    setLocalAdvertiseType(type);
    
    // Setze Default-Werte basierend auf dem Typ
    const newRange = type === "Mieten" 
      ? [100, 5000]
      : [10000, 5000000];
    
    setPriceRange(newRange);
    
    setFilters(prev => ({
      ...prev,
      advertise_type: type,
      minPrice: newRange[0],
      maxPrice: newRange[1]
    }));
  };

  const handlePriceRangeChange = (values) => {
    setPriceRange(values);
  };

  const handleApply = () => {
    setFilters(prev => ({
      ...prev,
      minPrice: priceRange[0],
      maxPrice: priceRange[1],
      advertise_type: localAdvertiseType
    }));
    onClose();
  };

  if (!isOpen) return null;

  const minValue = localAdvertiseType === "Mieten" ? 100 : 10000;
  const maxValue = localAdvertiseType === "Mieten" ? 5000 : 5000000;
  const step = localAdvertiseType === "Mieten" ? 10 : 1000;

  return createPortal(
    <div
      ref={dropdownRef}
      id="price-slider-dropdown"
      className="fixed z-[9999] bg-white border border-gray-200 rounded-md shadow-lg p-4 w-[300px]"
      style={{
        top: `${dropdownPosition.top}px`,
        left: `${dropdownPosition.left}px`,
      }}
    >
      {/* Toggle zwischen Mieten/Kaufen */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => handleAdvertiseTypeChange("Mieten")}
          className={`flex-1 px-3 py-2 text-sm font-helvetica rounded transition-colors ${
            localAdvertiseType === "Mieten"
              ? "bg-primary text-white"
              : "bg-gray-100 text-primary hover:bg-gray-200"
          }`}
        >
          Mieten
        </button>
        <button
          onClick={() => handleAdvertiseTypeChange("Kaufen")}
          className={`flex-1 px-3 py-2 text-sm font-helvetica rounded transition-colors ${
            localAdvertiseType === "Kaufen"
              ? "bg-primary text-white"
              : "bg-gray-100 text-primary hover:bg-gray-200"
          }`}
        >
          Kaufen
        </button>
      </div>

      {/* Preis-Label */}
      <div className="mb-2">
        <p className="text-sm font-helvetica font-semibold text-primary">Preis</p>
      </div>

      {/* Range Slider */}
      <div className="mb-4">
        <RangeSlider
          value={priceRange}
          onInput={handlePriceRangeChange}
          min={minValue}
          max={maxValue}
          step={step}
        />
      </div>

      {/* Preis-Anzeige */}
      <div className="flex justify-between mb-4">
        <div className="text-sm font-helvetica text-primary">
          <span className="font-medium">Von:</span> €{formatNumber(priceRange[0])}
        </div>
        <div className="text-sm font-helvetica text-primary">
          <span className="font-medium">Bis:</span> €{formatNumber(priceRange[1])}
        </div>
      </div>

      {/* Aktionen */}
      <div className="flex gap-2">
        <button
          onClick={onClose}
          className="flex-1 px-3 py-2 text-sm font-helvetica border border-gray-300 rounded hover:bg-gray-50 transition-colors"
        >
          Abbrechen
        </button>
        <button
          onClick={handleApply}
          className="flex-1 px-3 py-2 text-sm font-helvetica bg-primary text-white rounded hover:bg-primary/90 transition-colors"
        >
          Übernehmen
        </button>
      </div>
    </div>,
    document.body
  );
};
